import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import Swal from 'sweetalert2';

function Homepage({ onEventCreated }) {
  const [eventName, setEventName] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [eventTime, setEventTime] = useState("");
  const [eventVenue, setEventVenue] = useState("");
  const [attendees, setAttendees] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const formatName = (name) => {
    if (!name) return "";
    return name
      .trim()
      .toLowerCase()
      .split(/\s+/) // split by space(s)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };  

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
  
    setLoading(true);
  
    try {
      const fileResult = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (evt) => resolve(evt.target.result);
        reader.onerror = reject;
  
        // Use readAsText for CSV, readAsArrayBuffer for Excel
        if (file.name.endsWith(".csv")) {
          reader.readAsText(file);
        } else {
          reader.readAsArrayBuffer(file);
        }
      });
  
      const jsonData = await new Promise((resolve) => {
        setTimeout(() => {
          try {
            let rows = [];
            if (file.name.endsWith(".csv")) {
              // Parse CSV
              const workbook = XLSX.read(fileResult, { type: "string" });
              const firstSheetName = workbook.SheetNames[0];
              const worksheet = workbook.Sheets[firstSheetName];
              rows = XLSX.utils.sheet_to_json(worksheet, { raw: false });
            } else {
              // Parse Excel
              const data = new Uint8Array(fileResult);
              const workbook = XLSX.read(data, { type: "array" });
              const firstSheetName = workbook.SheetNames[0];
              const worksheet = workbook.Sheets[firstSheetName];
              rows = XLSX.utils.sheet_to_json(worksheet, { raw: false });
            }
  
            // Only keep `nric` and `name` columns
            const filteredRows = rows.map(row => ({
              nric: String(row.nric || row.NRIC || ""),
              name: formatName(row["Name"] || row.name),
            }));            
  
            resolve(filteredRows);
          } catch (err) {
            console.error("Parsing error:", err);
            resolve([]);
          }
        }, 0);
      });
  
      setAttendees(jsonData);
      console.log("Parsed rows:", jsonData.length);
  
    } catch (error) {
      console.error("File upload error:", error);
    } finally {
      setLoading(false);
    }
  };

  // Submit event + attendees to backend
  const handleCreate = async () => {
    if (!eventName || !eventDate || !eventTime) {
      Swal.fire({
        text: 'Please enter event name, date and time.',
        icon: 'error'
      });
      return;
    }

    const payload = {
      event_name: eventName,
      event_date: eventDate,
      event_time: eventTime,
      venue: eventVenue,
      attendees,
      status: 1
    };

    setLoading(true);

    try {
      const res = await fetch("/api/add/events", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Failed to create event");

      Swal.fire({
        text: 'Event created successfully!',
        icon: 'success'
      });
      // Reset form and attendees if needed
      setEventName("");
      setEventDate("");
      setEventTime("");
      setEventVenue("");
      setAttendees([]);

      // Trigger refresh in Sidebar
      if (onEventCreated) onEventCreated();

    } catch (error) {
      console.error(error);
      Swal.fire({
        text: 'Error creating event',
        icon: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {loading && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-[9999]">
          <div className="w-16 h-16 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
      <div>
        <h1 className="text-2xl font-bold text-black">Create Event</h1>

        <form className="space-y-4 pt-6 mb-7" onSubmit={(e) => e.preventDefault()}>
          <div className="flex items-center space-x-4">
            <label htmlFor="ename" className="whitespace-nowrap">
              Name:
            </label>
            <input
              id="ename"
              type="text"
              placeholder="Event Name"
              value={eventName}
              onChange={(e) => setEventName(e.target.value)}
              className="flex-grow px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
              required
            />
          </div>

          <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-6">
            <div className="flex items-center space-x-6 flex-grow">
              <label htmlFor="edate" className="whitespace-nowrap">
                Date:
              </label>
              <input
                id="edate"
                type="date"
                value={eventDate}
                onChange={(e) => setEventDate(e.target.value)}
                className="flex-grow px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
                required
              />
            </div>

            <div className="flex items-center space-x-6 flex-grow">
              <label htmlFor="etime" className="whitespace-nowrap">
                Time:
              </label>
              <div className="flex-grow flex items-center gap-2">
                <div className="flex-grow flex items-center border border-gray-300 rounded focus-within:ring-2 focus-within:ring-blue-400 px-4 py-2">
                  <input
                    type="text"
                    value={eventTime.split(':')[0]?.split(' ')[0] || ''}
                    onChange={(e) => {
                      const hours = e.target.value.replace(/\D/g, '').slice(0, 2);
                      const minutes = eventTime.split(':')[1]?.split(' ')[0] || '';
                      const period = eventTime.split(' ')[1] || 'AM';
                      setEventTime(`${hours}:${minutes} ${period}`);
                    }}
                    placeholder="HH"
                    maxLength="2"
                    className="w-12 px-2 text-center focus:outline-none"
                  />
                  <span className="text-gray-600">:</span>
                  <input
                    type="text"
                    value={eventTime.split(':')[1]?.split(' ')[0] || ''}
                    onChange={(e) => {
                      const minutes = e.target.value.replace(/\D/g, '').slice(0, 2);
                      const hours = eventTime.split(':')[0] || '';
                      const period = eventTime.split(' ')[1] || 'AM';
                      setEventTime(`${hours}:${minutes} ${period}`);
                    }}
                    placeholder="MM"
                    maxLength="2"
                    className="w-12 px-2 text-center focus:outline-none"
                  />
                  <select
                    value={eventTime.split(' ')[1] || 'AM'}
                    onChange={(e) => {
                      const timeValue = eventTime.split(' ')[0] || ':';
                      setEventTime(`${timeValue} ${e.target.value}`);
                    }}
                    className="ml-auto focus:outline-none bg-transparent"
                  >
                    <option value="AM">AM</option>
                    <option value="PM">PM</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-4 flex-grow">
              <label htmlFor="evenue" className="whitespace-nowrap">
                Venue:
              </label>
              <input
                id="evenue"
                type="text"
                placeholder="Event Venue"
                value={eventVenue}
                onChange={(e) => setEventVenue(e.target.value)}
                className="flex-grow px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
          </div>
        </form>

        <hr className="border-t-4 border-gray-300 my-4"></hr>

        <div className="mb-6">
          <label className="block mb-2 font-semibold">Upload Attendee List in Excel (nric, name):</label>
          <input type="file" accept=".xlsx, .xls, .csv" onChange={handleFileUpload} />
        </div>

        {attendees.length > 0 && (
          <>
          <h3 className="flex text-lg font-semibold mb-4 mt-2">List of Attendees:</h3>
          <div className="overflow-x-auto border rounded max-h-96">
            <table className="min-w-full table-auto border-collapse border border-gray-300">
              <thead className="bg-gray-200 sticky top-0">
                <tr>
                  <th className="border border-gray-300 px-4 py-2">No</th>
                  <th className="border border-gray-300 px-4 py-2">NRIC</th>
                  <th className="border border-gray-300 px-4 py-2">Name</th>
                  {/* <th className="border border-gray-300 px-4 py-2">Phone</th>
                  <th className="border border-gray-300 px-4 py-2">Table</th> */}
                </tr>
              </thead>
              <tbody>
                {attendees.map((attendee, i) => (
                  <tr key={i} className="even:bg-gray-50">
                    <td className="border border-gray-300 px-4 py-2">{i+1 || "-"}</td>
                    <td className="border border-gray-300 px-4 py-2">{attendee.nric || "-"}</td>
                    <td className="border border-gray-300 px-4 py-2">{attendee.name || "-"}</td>
                    {/* <td className="border border-gray-300 px-4 py-2">{attendee.phone || "-"}</td>
                    <td className="border border-gray-300 px-4 py-2">{attendee.table || "-"}</td> */}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}

        <div className="flex justify-end mt-6">
          <button
            onClick={handleCreate}
            className="w-40 font-bold bg-yellow-500 text-white py-2 rounded hover:bg-yellow-600 transition duration-200"
          >
            Create
          </button>
        </div>
      </div>
    </div>
  );
}

export default Homepage;